<template>
  <PreLoading
    v-if="preLoading"
    class="h-full"
  />

  <template v-else-if="checkInData != null && showCheckIn">
    <FirstCheckInView
      v-if="isFirstCheckIn"
      class="h-full"
      :check-data="checkInData"
      @done="onCheckInDone"
    ></FirstCheckInView>

    <CheckInView
      v-else
      class="h-full"
      :check-data="checkInData"
      @done="onCheckInDone"
    ></CheckInView>
  </template>

  <DailyTasksView
    v-else-if="dailyTaskResponse != null && showDailyTasks"
    :response="dailyTaskResponse"
    class="h-full"
    @done="onDailyTasksDone"
  ></DailyTasksView>

  <RankUpView
    v-else-if="afterRankGroup != null && showRankUp"
    :afterRank="afterRankGroup"
    :beforeRank="beforeRankGroup"
    @done="onBack"
  ></RankUpView>

  <template v-else-if="completedStats != null">
    <MasteryReview
      v-if="showMasteryReview"
      class="h-full bg-ld-background"
      :cards="masteryCards"
      @continue="switchToNextView('masteryReview')"
    ></MasteryReview>

    <ChallengeStageEndView
      v-else-if="store.stageUnit?.challenge"
      class="px-24px py-16px h-full bg-ld-background"
      :stat="completedStats"
      @continue="switchToNextView('lessonEnd')"
    ></ChallengeStageEndView>

    <LessonEndView
      v-else
      class="h-full bg-ld-background"
      :stat="completedStats"
      :mode="mode"
      @continue="switchToNextView('lessonEnd')"
      @review="onCardsReview"
    ></LessonEndView>
  </template>

  <RatioSpacedContainer
    v-else-if="store.stageUnit?.completed"
    class="text-center h-full bg-ld-background"
  >
    <template v-if="pending">
      <Loading />

      <div class="mt-2">正在提交本次学习数据</div>
    </template>

    <template v-else>
      <Icon
        name="error"
        class="w-250px"
      />

      <div class="vstack gap-3 mt-3 mb-8">
        <div class="text-21px font-semibold leading-none">
          {{ _t('api.network_error') }}
        </div>

        <div class="text-17px">
          {{ _t('api.check_network') }}
        </div>
      </div>

      <Button
        :label="_t('common.reapplay')"
        @click="completeReportAgain"
      ></Button>
    </template>
  </RatioSpacedContainer>

  <RatioSpacedContainer
    v-else-if="store.stageUnit == null"
    class="text-center h-full bg-ld-background"
  >
    {{ _t('learn.no_unit') }}

    <Button
      class="mt-4"
      label="返回首页"
      @click="onBackToHome"
    ></Button>
  </RatioSpacedContainer>

  <Loading
    v-else-if="pending"
    class="text-center h-full"
  ></Loading>

  <div
    v-else
    class="bg-gray-100 h-full relative"
  >
    <DebugLabel class="absolute top-4 left-1/2 transform -translate-x-1/2 z-10">
      <DebugButton
        v-if="packageId"
        label="完成关卡"
        size="small"
        @click="onDebugFinishStage"
      ></DebugButton>
    </DebugLabel>

    <div class="flex flex-col h-full mx-auto">
      <DuelQueue
        v-if="store.stageUnit.queueType === QueueType.Duel"
        @close="onClose"
        @left-blood-update="onLeftBloodUpdate"
      />

      <InsertQueue
        v-else
        :packageId="packageId"
        @close="onClose"
      />
    </div>
  </div>
</template>
<script setup lang="ts">
import { useCommonStore } from '@/stores/common-store'
import { QueueType, UnitEventType, type Card } from '@/types/core'
import DuelQueue from './DuelQueue/DuelQueue.vue'
import InsertQueue from './InsertQueue/InsertQueue.vue'
import { ref, computed, onMounted, onUnmounted, provide } from 'vue'
import { onBeforeRouteLeave, useRouter, useRoute } from 'vue-router'
import {
  handleUnitCancelled,
  completedStats,
  pending,
  completeReport,
  checkInData,
  dailyTaskResponse,
  mode,
  afterRankGroup,
  beforeRankGroup,
  masteryChanges,
} from './report'
import db from '@/db'
import PreLoading from './PreLoading.vue'
import { preLoadCards } from './preload'
import UnitReview from '@/pc/components/UnitReviewWithLoading.vue'
import MobileUnitReview from '@/mobile/pages/UnitReviewWithLoading.vue'
import CheckInView from '@/components/CheckInView.vue'
import FirstCheckInView from '@/components/FirstCheckInView/FirstCheckInView.vue'
import LessonEndView from '@/components/LessonEndView.vue'
import ChallengeStageEndView from '@/components/ChallengeStageEndView.vue'
import DailyTasksView from './DailyTasksView.vue'
import RankUpView from './RankUpView.vue'
import { markPopped } from '@/api/user'
import { checkNotificationGuide } from '@/utils/notification'
import MasteryReview from '@/components/MasteryReview.vue'
import { parseCard } from '@/utils/card'
import type { CardMasteryChange } from '@/api/learn'

provide('onCardStart', onCardStart)
provide('onCardEnd', onCardEnd)

const store = useCommonStore()
const router = useRouter()
const route = useRoute()

// 初始进来时学习单元并不一定处于可用状态，所以初始不能为 true
// 例如学习完成后刷新页面，此时单元仍为完成状态
const preLoading = ref(false)
const duelLeftBlood = ref(1)
const showDailyTasks = ref(false)
const showRankUp = ref(false)
const showMasteryReview = ref(false)
const masteryCards = ref<
  {
    card: Card
    mastery: number
    oldMastery?: number
  }[]
>([])

// 是否是首次签到
const isFirstCheckIn = computed(() => {
  if (checkInData.value == null) return false

  return checkInData.value.checkInCtRes.accumulatedDays === 1
})

const canShowDailyTasksView = computed(() => {
  if (dailyTaskResponse.value == null) return false

  // 如果是首次签到，不展示任务进度
  if (isFirstCheckIn.value) return false

  // 如果存在未领取的任务，不管完成未完成都要展示进度
  return dailyTaskResponse.value.tasks.some(item => !item.rewardClaimed)
})

const canShowRankUpView = computed(() => {
  return afterRankGroup.value != null
})

// 从路由参数中获取packageId
const packageId = computed(() => {
  const pkgId = route.query.packageId
  return pkgId ? Number(pkgId) : undefined
})

async function preLoadUnitCards() {
  preLoading.value = true

  if (store.stageUnit == null) {
    preLoading.value = false
    return
  }

  const cards: string[] = []
  store.stageUnit.schedules.forEach(item => {
    if (!store.stageUnit!.completedCards?.includes(item.cardId)) {
      cards.push(item.content)
    }
  })

  // bridge 的 fetch 目前实现有问题，在有道返回 500 的
  // 情况下，会直接导致 js runtime error 而不是 promise reject
  // 这里加一个兜底
  setTimeout(() => {
    preLoading.value = false
  }, 5000)

  await preLoadCards(cards)

  preLoading.value = false
}

onInit(() => {
  store.checkStageUnit()

  // 如果学习单元已经完成了，则自动上报一次即可，拿到上报完成的统计信息
  if (store.stageUnit?.completed) {
    completeReportAgain()
    return
  }

  preLoadUnitCards()
})

onMounted(() => {
  // window.onbeforeunload = () => true

  if (_global.isInsideApp) {
    // 进入学习页面后 禁用 ios 手势返回
    _bridge.disableBackGesture()
    // 用于学习中断后恢复
    _bridge.setInitPath(location.pathname)
  }
})

onUnmounted(() => {
  // 离开页面后 启用 ios 手势返回
  if (_global.isInsideApp) {
    _bridge.enableBackGesture()
    // 退出页面设置为空
    _bridge.setInitPath('')
  }

  window.onbeforeunload = null
})

onBeforeRouteLeave(async (_to, _from, next) => {
  const quitConfirmed = await quitConfirm()

  if (quitConfirmed) {
    if (!store.stageUnit?.completed) {
      handleUnitCancelled()
    }

    if (store.stageUnit) {
      delete db.unitProgressCacheV2[store.stageUnit.queueType]
      delete db.unitProgressCacheV2[QueueType.Duel]
    }

    next()
  } else {
    next(false)
  }
})

function onCardsReview() {
  if (_global.isPcMode) {
    _openDialog(UnitReview, {
      rootClass: 'min-w-600px max-w-900px min-h-600px',
      props: {
        stageId: store.stageUnit!.atlasStageId,
      },
      dialog: {
        showHeader: false,
        pt: {
          content: {
            style: 'flex:1; padding: 0px;',
          },
        },
      },
    })
  } else {
    _openDialog(MobileUnitReview, {
      fullscreenInMobile: true,
      props: {
        stageId: store.stageUnit!.atlasStageId,
      },
      dialog: {
        showHeader: false,
        pt: {
          content: {
            style: 'padding: 0px;',
          },
        },
      },
    })
  }
}

async function quitConfirm(): Promise<boolean> {
  // 因为用户可以直接通过链接进来学习页面，所以学习页面并不能保证 stageUnit 一定存在
  if (store.stageUnit == null) {
    return true
  }

  const stageUnit = store.stageUnit!

  const events = stageUnit.events
  const completeCardsCount = stageUnit.completedCards.length ?? 0

  if (stageUnit.completed) return true

  // 如果已经学习过，退出时需要提示
  const hasEvent =
    Object.values(events).some(item => item.length > 0) ||
    completeCardsCount > 0

  if (hasEvent) {
    let wantQuit = false

    await _confirm({
      scene: 'warn',
      icon: {
        name: 'energy-broken',
        type: 'svg',
      },
      content: _t('learn.quit_tip'),
      primaryText: _t('learn.quit_cancel'),
      secondaryText: _t('learn.quit_confirm'),
      onSecondaryClick(resolve) {
        wantQuit = true

        resolve(true)
      },
    })
    return wantQuit
  }

  return true
}

function completeReportAgain() {
  if (store.stageUnit == null) return

  if (store.stageUnit.queueType === QueueType.Duel) {
    completeReport(duelLeftBlood.value)
  } else {
    completeReport()
  }
}

function onLeftBloodUpdate(val: number) {
  duelLeftBlood.value = val
}

async function onClose() {
  onBack()
}
const showCheckIn = ref(false)

// 处理学习完成后的视图切换
function switchToNextView(fromView: 'lessonEnd' | 'masteryReview') {
  console.log('switchToNextView', fromView)
  console.log('masteryChanges', masteryChanges.value)

  // 如果是从掌握度视图返回，先关闭它
  if (fromView === 'masteryReview') {
    showMasteryReview.value = false
  }

  // 如果是从学习结束视图开始，且有掌握度数据，先展示掌握度
  if (fromView === 'lessonEnd' && masteryChanges.value) {
    // 将掌握度变化数据转换为卡片数据
    masteryCards.value = Object.entries(masteryChanges.value)
      .map(([cardId, change]) => {
        const schedule = store.stageUnit!.schedules.find(
          s => s.cardId === Number(cardId)
        )
        if (!schedule) return null

        try {
          const card = JSON.parse(schedule.content) as Card
          const parsedCard = parseCard(card)
          if (!parsedCard.card) return null

          return {
            card: parsedCard.card,
            mastery: change.newMastery,
            oldMastery: change.oldMastery,
          }
        } catch (e) {
          console.error('Failed to parse card:', e)
          return null
        }
      })
      .filter((item): item is NonNullable<typeof item> => item !== null)

    console.log('masteryCards', masteryCards.value)
    if (masteryCards.value.length > 0) {
      console.log('showMasteryReview')
      showMasteryReview.value = true
      return
    }
  }

  // 按顺序展示后续视图
  if (checkInData.value) {
    // 上报签到
    markPopped()
    showCheckIn.value = true
  } else if (canShowDailyTasksView.value) {
    showDailyTasks.value = true
  } else if (canShowRankUpView.value) {
    showRankUp.value = true
  } else {
    onBack()
  }
}

function onCheckInDone() {
  if (canShowDailyTasksView.value) {
    showCheckIn.value = false
    showDailyTasks.value = true
  } else if (canShowRankUpView.value) {
    showCheckIn.value = false
    showRankUp.value = true
  } else {
    onBack()
  }
}

function onDailyTasksDone() {
  if (canShowRankUpView.value) {
    showDailyTasks.value = false
    showRankUp.value = true
  } else {
    onBack()
  }
}

function onBack() {
  if (completedStats.value) {
    // 学习完成退出时检查是否需要展示通知引导
    if (_global.isInsideApp) {
      checkNotificationGuide()
    }
  }

  if (router.canGoBack) {
    router.back()
    return
  }

  router.replace({
    name: 'atlas',
    query: {
      challengeStage: store.stageUnit?.challenge?.index,
    },
  })
}

let cardStartTime = 0
function onCardStart() {
  cardStartTime = Math.floor(Date.now() / 1000)
}

function onCardEnd(cardId: number) {
  const end = Math.floor(Date.now() / 1000)

  if (cardStartTime > 0) {
    store.learnCard(cardId)
    store.addCardDuration(cardId, end - cardStartTime)
  }
}

function onBackToHome() {
  router.push(_global.homePage)
}

async function onDebugFinishStage() {
  if (!packageId.value || !store.stageUnit) return

  try {
    // 模拟所有卡片都已完成学习
    const cardIds = store.stageUnit.schedules.map(item => item.cardId)

    // 为每个卡片添加模拟的学习事件和时长
    for (const cardId of cardIds) {
      // 模拟学习事件
      store.insertLearningEvent(cardId, {
        event: UnitEventType.CORRECT,
        timestamp: Date.now(),
      })

      // 模拟学习时长和次数
      store.addCardDuration(cardId, 10)
      store.learnCard(cardId)
      store.learnCard(cardId) // 学习2次

      // 标记卡片为已完成
      store.completeCard(cardId)
    }

    // 设置最大连击数
    store.setComboCount(cardIds.length)

    // 调用正常的完成上报流程
    completeReport()

    // 等待上报完成
    while (pending.value) {
      await new Promise(resolve => setTimeout(resolve, 100))
    }

    _message.success('关卡已完成')
  } catch (error) {
    console.error('Debug finish stage error:', error)
    _message.error('完成关卡时出错')
  }
}
</script>
<style scoped></style>
